import { useEffect, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { useCardsProductsStore, useTokenStore } from '@lib/store';
import type { GetAllCardsResponse, GetProductsResponse } from '@shared/api';
import { Tabs } from '@shared/widgets';
import { CardSlider } from '@widgets/cardSlider';
import { CardInfo } from '@widgets/cardInfo';

let swiperControl: SwiperType | null = null;

export const InsigniaCards = () => {
  const cardsProductsStore = useCardsProductsStore();
  const tokenStore = useTokenStore();

  const [currentTab, setCurrentTab] = useState(0);
  const [shownCard, setShownCard] = useState<number>(0);
  const [filteredCards, setFilteredCards] = useState<GetProductsResponse[]>([]);

  useEffect(() => {
    return () => {
      swiperControl = null;
    };
  }, []);

  useEffect(() => {
    cardsProductsStore.get();
  }, [tokenStore.token]);

  useEffect(() => {
    setFilteredCards(cardsProductsStore.data.filter((i) => i.benefits.length));
  }, [cardsProductsStore.data]);

  useEffect(() => {
    swiperControl?.slideTo(0);
  }, [currentTab]);

  const renderCards = (type: string) => <div className='mt-[118px] max-md:m-0'>{renderTabContent(type)}</div>;
  const renderTabContent = (type: string) => {
    switch (type) {
      case 'all':
        return renderAll();
      case 'personal':
        return renderPersonal();
      case 'business':
        return renderBusiness();
      default:
        return null;
    }
  };

  const handleCardSelect = (card: GetAllCardsResponse, index: number) => {
    setShownCard(index);
    swiperControl?.slideTo(index);
  };

  const renderAll = () => (
    <div className='w-full max-md:mt-12'>
      <CardSlider
        showNameOverCard
        className='min-h-31.25! bg-transparent!'
        cardClassName='!w-[169px] pr-0 object-contain relative max-md:pl-4 max-md:!w-[75vw] max-md:!pl-4 smallCardsSlider'
        imageClassName='h-auto !w-[169px] !rounded-lg shadow-[0_9px_16px_0_#00000029] max-md:!w-full'
        isNeedSeparator
        onSelectCard={handleCardSelect}
        cards={filteredCards}
        toggleCards
        showCardId={shownCard}
        cardImage='second'
      />
      <div className='mt-[88px]'>
        {filteredCards && (
          <CardInfo
            className='max-md:!block max-md:!min-h-0'
            cards={[...filteredCards]}
            shownCard={shownCard}
            onChange={(swiper) => setShownCard(swiper.activeIndex)}
            onSwiper={(swiper) => (swiperControl = swiper)}
          />
        )}
      </div>
    </div>
  );

  const renderPersonal = () => (
    <div className='px-[94px] max-md:mt-12 max-md:p-0'>
      <CardSlider
        showNameOverCard
        cardClassName='!w-[169px] pr-0 object-contain relative max-md:pl-4 max-md:!w-[75vw] max-md:!pl-4'
        imageClassName='h-auto !w-[169px] shadow-[0_9px_16px_0_#00000029] max-md:!w-full'
        className='min-h-31.25! bg-transparent!'
        isNeedSeparator
        onSelectCard={handleCardSelect}
        cards={filteredCards.filter((i) => i.category === 'personal')}
        toggleCards
        showCardId={shownCard}
        cardImage='second'
      />
      <div className='mt-[88px]'>
        {filteredCards && (
          <CardInfo
            className='max-md:!block max-md:!min-h-0'
            cards={[...filteredCards.filter((i) => i.category === 'personal')]}
            shownCard={shownCard}
            onChange={(swiper) => setShownCard(swiper.activeIndex)}
            onSwiper={(swiper) => (swiperControl = swiper)}
          />
        )}
      </div>
    </div>
  );

  const renderBusiness = () => (
    <div className='px-[94px] max-md:mt-12 max-md:p-0'>
      <CardSlider
        showNameOverCard
        className='min-h-31.25! bg-transparent!'
        cardClassName='!w-[169px] pr-0 object-contain relative max-md:pl-4 max-md:!w-[75vw] max-md:!pl-4'
        imageClassName='h-auto !w-[169px] shadow-[0_9px_16px_0_#00000029] max-md:!w-full'
        isNeedSeparator
        onSelectCard={handleCardSelect}
        cards={filteredCards.filter((i) => i.category === 'business')}
        toggleCards
        showCardId={shownCard}
        cardImage='second'
      />
      <div className='mt-[88px]'>
        {filteredCards && (
          <CardInfo
            className='max-md:!block max-md:!min-h-0'
            cards={[...filteredCards.filter((i) => i.category === 'business')]}
            shownCard={shownCard}
            onChange={(swiper) => setShownCard(swiper.activeIndex)}
            onSwiper={(swiper) => (swiperControl = swiper)}
          />
        )}
      </div>
    </div>
  );

  const tabs = [
    { label: 'All', content: renderCards('all') },
    { label: 'Personal', content: renderCards('personal') },
    { label: 'Business', content: renderCards('business') },
  ];

  return (
    <div className='overflow-hidden'>
      <h2 className='mt-[111px] w-full text-center font-[Prata] text-[56px] text-[#272727] max-md:m-0 max-md:text-[28px]'>Insignia Cards</h2>
      <p className='mt-4 mb-[55px] text-center text-lg font-light text-[#4c4c4c] max-md:px-10 max-md:text-base max-md:text-[#4c4c4c80]'>
        Explore some of our other super-premium, premium, and corporate payment card solutions.
      </p>
      <Tabs tabs={tabs} onChange={setCurrentTab} />
      {!!filteredCards?.[shownCard]?.benefits?.length && (
        <div className='relative mt-[58px] px-[165px] max-md:px-0'>
          <div className='flex items-center justify-center max-md:mx-4'>
            <div className='h-[1px] w-full bg-[#272727]' />
            <h3 className='object-contain px-4 text-center font-[Prata] text-2xl whitespace-nowrap text-[#272727] max-md:m-0 max-md:p-0 max-md:text-[28px]'>
              KEY BENEFITS
            </h3>
            <div className='h-[1px] w-full bg-[#272727]' />
          </div>
          <div className='mt-[65px] flex shrink-0 basis-[100%] justify-center max-md:flex-col max-md:items-center max-md:p-4'>
            {filteredCards[shownCard].benefits.map((item, idx) => (
              <div
                key={idx}
                className='mx-[10px] flex min-h-[390px] max-w-[350px] shrink-0 basis-[33%] flex-col bg-[#fff] px-[41px] py-[31px] max-md:m-0 max-md:mb-6 max-md:min-h-0 max-md:min-w-0 max-md:bg-[#fff] max-md:p-4'
              >
                <img className='mx-auto h-[40px] w-[40px] max-md:hidden' src={`${process.env.API_URL}/${item.icon}`} alt='icon' />
                <img className='mx-auto mb-6 hidden w-[80px] max-md:block' src={`/assets/${item.icon}`} alt='icon' />
                <div className='mt-[21px] min-h-[50px] text-center font-[Prata] text-lg text-[#272727] max-md:min-h-0 max-md:px-[40px]'>
                  {item.title}
                </div>
                <div className='mx-auto my-4 h-[1px] w-[49px] bg-[#d7c29a]' />
                <div className='text-center text-sm font-light text-[#4c4c4c] max-md:text-base max-md:text-[#4c4c4c80]'>{item.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
